import { useEffect, useMemo, useState } from 'react';

interface UsePaginationProps<T> {
	data: Array<T>;
	itemsPerPage?: number;
}

interface UsePaginationReturn<T> {
	currentPage: number;
	totalPages: number;
	paginatedData: Array<T>;
	goToPage: (page: number) => void;
	goToNextPage: () => void;
	goToPreviousPage: () => void;
	canGoNext: boolean;
	canGoPrevious: boolean;
	startIndex: number;
	endIndex: number;
	totalItems: number;
}

/**
 * Client-side pagination hook.
 * Slices an in-memory array into pages and provides navigation helpers.
 * Automatically resets to page 1 when the data length changes.
 */
export function usePagination<T>({
	data,
	itemsPerPage = 10,
}: UsePaginationProps<T>): UsePaginationReturn<T> {
	const [currentPage, setCurrentPage] = useState(1);

	const totalPages = Math.max(1, Math.ceil(data.length / itemsPerPage));
	const totalItems = data.length;

	// Reset to page 1 when data changes
	useEffect(() => {
		setCurrentPage(1);
	}, [data.length]);

	// Keep current page within bounds when total pages shrinks
	useEffect(() => {
		if (currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	const { paginatedData, startIndex, endIndex } = useMemo(() => {
		const validPage = Math.min(Math.max(1, currentPage), totalPages);
		const start = (validPage - 1) * itemsPerPage;
		const end = Math.min(start + itemsPerPage, data.length);

		return {
			paginatedData: data.slice(start, end),
			startIndex: data.length > 0 ? start + 1 : 0,
			endIndex: end,
		};
	}, [data, currentPage, totalPages, itemsPerPage]);

	const goToPage = (page: number): void => {
		const newPage = Math.max(1, Math.min(page, totalPages));
		setCurrentPage(newPage);
	};

	const goToNextPage = (): void => {
		goToPage(currentPage + 1);
	};

	const goToPreviousPage = (): void => {
		goToPage(currentPage - 1);
	};

	const canGoNext = currentPage < totalPages;
	const canGoPrevious = currentPage > 1;

	return {
		currentPage,
		totalPages,
		paginatedData,
		goToPage,
		goToNextPage,
		goToPreviousPage,
		canGoNext,
		canGoPrevious,
		startIndex,
		endIndex,
		totalItems,
	};
}

/**
 * Generate an array of page numbers to display in the pagination UI.
 * Centers around the current page and caps at maxButtons.
 */
export function getPageNumbers(
	currentPage: number,
	totalPages: number,
	maxButtons: number = 5,
): Array<number> {
	let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
	const endPage = Math.min(totalPages, startPage + maxButtons - 1);

	if (endPage - startPage + 1 < maxButtons) {
		startPage = Math.max(1, endPage - maxButtons + 1);
	}

	const length = endPage - startPage + 1;
	return Array.from({ length }, (_, i) => startPage + i);
}
